/**
 * Drop-in remplacement de `window.confirm(...)` aligné sur la charte
 * graphique de l'app (cf. modal preset existant dans
 * FeatureFlagsManagement.vue : fond noir/blur, bg-card, border
 * gray-700, boutons Annuler/Confirmer en bas).
 *
 * État singleton dans `confirmState` ; le modal global est monté
 * dans App.vue (ConfirmModal.vue) et observe ce ref. La fonction
 * `confirm(opts)` renvoie une Promise<boolean> qui se résout au
 * clic Confirmer (true) / Annuler / clic-outside (false).
 *
 * Usage :
 *   const { confirm } = useConfirm();
 *   const ok = await confirm({
 *     title: "Activer le contrat",
 *     message: "Cette action démarrera la facturation. Continuer ?",
 *     confirmLabel: "Activer",
 *     tone: "primary",
 *   });
 *   if (!ok) return;
 *
 * Tone :
 *   - "primary" (défaut) : bouton vert #219152 — actions normales
 *   - "warning"          : bouton jaune-600 — actions sensibles
 *   - "danger"           : bouton rouge-700 — destructions
 */
import { ref } from "vue";

export type ConfirmTone = "primary" | "warning" | "danger";

export interface ConfirmOptions {
	title: string;
	message?: string;
	/** Lignes additionnelles affichées en gris sous le message principal. */
	details?: string[];
	confirmLabel?: string;
	cancelLabel?: string;
	tone?: ConfirmTone;
}

interface ConfirmState extends ConfirmOptions {
	open: boolean;
	resolve?: (value: boolean) => void;
}

// Singleton — un seul confirm visible à la fois (multi-confirm
// imbriqué = bug UX, on l'évite en remplaçant le précédent par le
// nouveau et en résolvant l'ancien à false).
const confirmState = ref<ConfirmState>({
	open: false,
	title: "",
});

export function useConfirm() {
	const confirm = (opts: ConfirmOptions): Promise<boolean> => {
		return new Promise<boolean>((resolve) => {
			// Si un confirm précédent traîne ouvert, on le résout négatif
			// pour libérer le caller — sinon il pendrait éternellement.
			if (confirmState.value.open && confirmState.value.resolve) {
				confirmState.value.resolve(false);
			}
			confirmState.value = {
				open: true,
				title: opts.title,
				message: opts.message,
				details: opts.details,
				confirmLabel: opts.confirmLabel ?? "Confirmer",
				cancelLabel: opts.cancelLabel ?? "Annuler",
				tone: opts.tone ?? "primary",
				resolve,
			};
		});
	};

	const accept = () => {
		const r = confirmState.value.resolve;
		confirmState.value = { open: false, title: "" };
		r?.(true);
	};

	const cancel = () => {
		const r = confirmState.value.resolve;
		confirmState.value = { open: false, title: "" };
		r?.(false);
	};

	return { confirm, accept, cancel, state: confirmState };
}

// Export direct pour les composants n'ayant besoin que de l'état
// (le ConfirmModal global lit ces deux refs/fns sans appeler
// useConfirm() pour rester léger).
export { confirmState };
